// Client-side glue for the cookie-based Supabase session.
//
// 1) migrateLegacyLocalStorageSession() — one-time move of an existing
//    localStorage session into the cookie session, so the switch doesn't log
//    anyone out.
// 2) syncSessionToServer() — asks the server to re-issue the current session as
//    first-party `Set-Cookie` headers (not writable/expirable by ITP the way
//    document.cookie is).
import { supabase } from "@/integrations/supabase/client";
import { getSupabaseBrowserEnv, supabaseUsesCookieStorage } from "@/integrations/supabase/client";
import { supabaseStorageKey } from "@/integrations/supabase/cookie-config";
import type { Session } from "@supabase/supabase-js";

const SYNC_URL = "/api/public/auth/session-sync";

function decodeStored(raw: string): unknown {
  const value = raw.startsWith("base64-") ? atob(raw.slice("base64-".length)) : raw;
  return JSON.parse(value);
}

export async function migrateLegacyLocalStorageSession(): Promise<void> {
  if (typeof window === "undefined") return;
  // When cookies aren't writable (editor preview iframe), localStorage IS the
  // active session store — migrating (and clearing) it would sign the user out.
  if (!supabaseUsesCookieStorage()) return;

  let key: string;
  try {
    key = supabaseStorageKey(getSupabaseBrowserEnv().SUPABASE_URL);
  } catch {
    return;
  }

  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(key);
  } catch {
    return;
  }
  if (!raw) return;

  try {
    const { data } = await supabase.auth.getSession();
    if (!data.session) {
      const stored = decodeStored(raw) as { access_token?: string; refresh_token?: string };
      if (stored?.access_token && stored?.refresh_token) {
        await supabase.auth.setSession({
          access_token: stored.access_token,
          refresh_token: stored.refresh_token,
        });
      }
    }
  } catch {
    // corrupt legacy value — just drop it
  } finally {
    try {
      window.localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  }
}

export async function syncSessionToServer(session: Session | null): Promise<void> {
  if (typeof window === "undefined") return;
  try {
    await fetch(SYNC_URL, {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(
        session
          ? { access_token: session.access_token, refresh_token: session.refresh_token }
          : { signOut: true },
      ),
    });
  } catch {
    // Non-fatal: the client still holds the session; the next auth event retries.
  }
}
